import React, { useEffect } from 'react';
import { FIX_ERROR } from '../configs/dialog_configs';
import {
  PrimeReactButton,
  PrimeReactInputSwitch,
  PrimeReactInputText,
  PrimeReactMultiSelect,
  PrimeReactTooltip,
  PrimeReactDropdown,
} from 'components/common';
import {
  automationErrorLogCodeOptions,
  automationErrorLogCodes,
  formControlLabels,
  formControlNames,
  reactHookFormRegisterOptions,
} from 'constants/automationConstants';
import { getOnlyNumbersFromString } from 'utils/utils';
import NumberFormat from 'react-number-format';
import { Controller } from 'react-hook-form';
import { teamMemberRolesList } from 'constants/automationConstants';
import { validatePhoneNumber } from 'modules/lead/helpers/helpers';
import { checkOptionDisabled } from 'modules/settings/workflow_automation/helpers/automationCommonHelpers';
import { MultiSelect } from 'primereact/multiselect';

const {
  userNotInCase,
  noEmail,
  noLawftAccess,
  noBillingContact,
  noPhoneNumber,
  noLeadAttorney,
  noLeadAssociate,
  noLeadParalegal,
  noLeadCaseManager,
  noLeadTeamMember,
  userArchived,
  userUnconfirmed,
  senderArchived,
  senderUnconfirmed,
  groupMembersArchived,
  groupMembersUnconfirmed,
} = automationErrorLogCodes || {};

const generateErrorCodeLabel = (errorFixDialogData) => {
  try {
    const { error_code } = errorFixDialogData || {};
    if (automationErrorLogCodeOptions?.length) {
      let result = automationErrorLogCodeOptions?.find((item) => item?.value === error_code);
      return result?.label ? ` - ${result?.label}` : '';
    }
  } catch (error) {
    console.log('error', error);
  }
};

const displayFormError = (error) => {
  return <>{error?.message ? <div className="text-danger error-log-validation mt-1">{error?.message}</div> : null}</>;
};

const handlePhoneNumberChange = (e, handleChange) => {
  e.target.value = getOnlyNumbersFromString(e?.target?.value);
  handleChange(e);
};

const generateDynamicFormControlsLabel = (errorFixDialogData, formControlLabels) => {
  if (errorFixDialogData?.action_code === 'ACT_EVENT_CREATE') {
    return formControlLabels?.attendees;
  } else if (errorFixDialogData?.action_code === 'ACT_TASK_CREATE') {
    return formControlLabels?.assignees;
  } else if (errorFixDialogData?.action_code === 'ACT_SEND_SECURE_MESSAGE') {
    return formControlLabels?.to;
  }

  return null; // Or you can return a default value if needed
};

const generateDynamicFormControlsErrorCode = (errorFixDialogData) => {
  if (errorFixDialogData?.error_code === userArchived) {
    return userArchived;
  } else if (errorFixDialogData?.error_code === userUnconfirmed) {
    return userUnconfirmed;
  } else if (errorFixDialogData?.error_code === senderArchived) {
    return senderArchived;
  } else if (errorFixDialogData?.error_code === senderUnconfirmed) {
    return senderUnconfirmed;
  } else if (errorFixDialogData?.error_code === groupMembersArchived) {
    return groupMembersArchived;
  } else if (errorFixDialogData?.error_code === groupMembersUnconfirmed) {
    return groupMembersUnconfirmed;
  }

  return null;
};

const assignToItemTemplate = (option) => {
  return (
    <>
      {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
      {option?.label}
    </>
  );
};

/** --- generateDynamicFormControls - This function generates form-controls like inputs etc.. dynamically --- */
const generateDynamicFormControls = (params) => {
  const {
    label: formControlLabel = '',
    name: formControlName = '',
    isRequired = true,
    control = '',
    rules: validationRules = {},
    errors: validationErrors = {},
    errorCode = '',
    getValues = () => {},
    watch = () => {},
    options = [],
  } = params || {};

  const renderComponent = (field) => {
    const { value = '', name = '', onChange = () => {} } = field || {};

    const { email, lawftAccess, phone, billingContacts, teamMembers, firmUsers, senderUser } = formControlNames || {};

    let component = null;

    switch (name) {
      case email:
        component = (
          <PrimeReactInputText
            className="input-shadow w-100 border-0"
            placeholder={formControlLabels?.email}
            value={value}
            name={name}
            // onChange={(e) => {
            //   console.log('_e_', e);
            //   onChange(e);
            // }}
            onChange={(e) => {
              const trimmedValue = e?.target?.value?.trim();
              onChange({
                ...e,
                target: {
                  ...e.target,
                  name: name,
                  value: trimmedValue,
                },
              });
            }}
          />
        );
        break;
      case lawftAccess:
        let isChecked = errorCode === noLawftAccess ? getValues('lawftAccess') || watch('email') : true;
        let isDisabled = errorCode === noEmail;
        component = (
          <PrimeReactInputSwitch
            className="input-swich"
            value={value}
            name={name}
            id={name}
            isChecked={isChecked}
            isDisabled={isDisabled}
            onChange={(e) => {
              if (getValues('lawftAccess') || watch('email')) onChange(e);
            }}
          />
        );

        break;
      case phone:
        component = (
          <NumberFormat
            className="input-shadow w-100 p-2 border-0 number-format p-inputtext"
            placeholder="Phone"
            value={value}
            name={name}
            format="(###) ###-####"
            mask="_"
            onChange={(e) => {
              handlePhoneNumberChange(e, onChange);
            }}
          />
        );
        break;
      case billingContacts:
        component = (
          <PrimeReactMultiSelect
            className="me-2 input-shadow w-100 F-size14 searchable-dropdown"
            options={options}
            value={value}
            name={name}
            onChange={(e) => {
              onChange(e);
            }}
            placeholder="Select"
            optionDisabled={checkOptionDisabled}
            display={value?.length ? 'chip' : ''}
            filter
            filterPlaceholder="Search"
            resetFilterOnHide
          />
        );
        break;
      case teamMembers:
        // const uniqueOptions = options.filter(
        //   (option, index, self) => index === self.findIndex((o) => o.client_cognito_username === option.client_cognito_username)
        // );
        const uniqueOptions = options.filter(
          (option, index, self) =>
            index === self.findIndex((o) => o.client_cognito_username === option.client_cognito_username) &&
            option.is_confirmed === true &&
            option.status === 'ACTIVE'
        );
        component = (
          <PrimeReactDropdown
            className="me-2 input-shadow w-100 F-size14 searchable-dropdown"
            options={uniqueOptions}
            value={value}
            name={name}
            onChange={(e) => {
              onChange(e);
            }}
            placeholder="Select"
            display={value?.length ? 'chip' : ''}
            filter
            filterPlaceholder="Search"
            resetFilterOnHide
            itemTemplate={assignToItemTemplate}
          />
        );
        break;
      case firmUsers:
        const newGroup = {
          groupLabel: 'Team Member Roles',
          code: 'TeamMemberRoles',
          items: [...teamMemberRolesList],
        };

        // Insert the new group at the second position
        const updatedOptions = [
          options[0], // First existing group
          newGroup, // Your new group
          ...options.slice(1), // Remaining existing groups
        ];

        // Create a set to track unique group labels
        const uniqueGroupLabels = new Set();

        // Filter out duplicate groups based on groupLabel
        const uniqueUpdatedOptions = updatedOptions?.filter((option) => {
          if (!uniqueGroupLabels?.has(option?.groupLabel)) {
            uniqueGroupLabels?.add(option?.groupLabel);
            return true;
          }
          return false;
        });

        component = (
          <PrimeReactMultiSelect
            className="me-2 input-shadow w-100 F-size14 searchable-dropdown"
            options={uniqueUpdatedOptions || []}
            value={value}
            name={name}
            onChange={(e) => {
              onChange(e);
            }}
            placeholder="Select"
            display={value?.length ? 'chip' : ''}
            filter
            filterPlaceholder="Search"
            resetFilterOnHide
            optionLabel="label"
            optionGroupLabel="groupLabel"
            optionGroupChildren="items"
            itemTemplate={assignToItemTemplate}
          />
        );
        break;
      case senderUser:
        const newSenderGroup = {
          groupLabel: 'Team Member Roles',
          code: 'TeamMemberRoles',
          items: [...teamMemberRolesList],
        };

        // Insert the new group at the second position
        const updatedSenderOptions = [
          options[0], // First existing group
          newSenderGroup, // Your new group
          ...options.slice(1), // Remaining existing groups
        ];

        // Create a set to track unique group labels
        const uniqueSenderGroupLabels = new Set();

        // Filter out duplicate groups based on groupLabel
        const uniqueSenderUpdatedOptions = updatedSenderOptions?.filter((option) => {
          if (!uniqueSenderGroupLabels?.has(option?.groupLabel)) {
            uniqueSenderGroupLabels?.add(option?.groupLabel);
            return true;
          }
          return false;
        });
        component = (
          <PrimeReactDropdown
            className="me-2 input-shadow w-100 F-size14 searchable-dropdown"
            options={uniqueSenderUpdatedOptions}
            value={value}
            name={name}
            onChange={(e) => {
              onChange(e);
            }}
            placeholder="Select"
            display={value?.length ? 'chip' : ''}
            filter
            filterPlaceholder="Search"
            resetFilterOnHide
            optionLabel="label"
            optionGroupLabel="groupLabel"
            optionGroupChildren="items"
            itemTemplate={assignToItemTemplate}
          />
        );
        break;
      default:
        break;
    }

    return component;
  };

  return (
    <>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-lg-4 col-12 p-medium">
          {formControlLabel}
          {isRequired ? (
            <label htmlFor="" className="text-danger ms-1">
              *
            </label>
          ) : null}
        </div>
        <div className="col-lg-8 col-12 d-flex align-items-center">
          <Controller name={formControlName} control={control} rules={validationRules} render={({ field }) => renderComponent(field)} />
        </div>

        {displayFormError(validationErrors[formControlName])}
      </div>
    </>
  );
};

/** --- displayUiErrorCodeWise - This function displays dynamic forms and components according to the error-code --- **/
const displayUiErrorCodeWise = (props) => {
  const {
    errorFixDialogData,
    billingContactsOptions,
    teamMembersList,
    fullAttendeesList,
    messageRecipientsList,
    reactHookForm: {
      control,
      watch,
      getValues,
      formState: { errors },
    },
  } = props || {};

  let conditionBasedUi = null;
  const { error_code = '', user_name = '' } = errorFixDialogData || {};

  if (error_code === userNotInCase) {
    conditionBasedUi = (
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-lg-4 col-12 p-medium">
          <label htmlFor="">{formControlLabels?.firmUser}</label>
        </div>
        <div className="col-lg-8 col-12 d-flex align-items-center">
          <div name="firmUser" className="fw-bold">
            {errorFixDialogData?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
            {user_name || 'N/A'}
          </div>
        </div>
      </div>
    );
  } else if (error_code === noEmail) {
    let lawftAccessCondition = watch('email') && !errors['email'];

    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: formControlLabels?.email,
          name: formControlNames?.email,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.email,
          errors,
          errorCode: noEmail,
        })}

        {lawftAccessCondition
          ? generateDynamicFormControls({
              label: formControlLabels?.enableLawftAccess,
              name: formControlNames?.lawftAccess,
              isRequired: false,
              control,
              rules: {},
              errors: {},
              errorCode: noEmail,
              getValues,
              watch,
            })
          : null}
      </>
    );
  } else if (error_code === noLawftAccess) {
    let tootlTipCondition = !watch('email') && !watch('lawftAccess');

    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: formControlLabels?.email,
          name: formControlNames?.email,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.email,
          errors,
          errorCode: noLawftAccess,
        })}

        {generateDynamicFormControls({
          label: formControlLabels?.enableLawftAccess,
          name: formControlNames?.lawftAccess,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.lawftAccess,
          errors,
          errorCode: noLawftAccess,
          getValues,
          watch,
        })}
        {tootlTipCondition ? (
          <PrimeReactTooltip target={`#lawftAccess`} position="bottom" className="case-tooltip">
            {'Add Email To Enable Lawft Access'}
          </PrimeReactTooltip>
        ) : null}
      </>
    );
  }
  // else if (error_code === noPhoneNumber) {
  //   conditionBasedUi = (
  //     <>
  //       {generateDynamicFormControls({
  //         label: formControlLabels?.phoneNumber,
  //         name: formControlNames?.phone,
  //         isRequired: true,
  //         control,
  //         rules: reactHookFormRegisterOptions?.phone,
  //         errors,
  //         errorCode: noPhoneNumber,
  //       })}
  //     </>
  //   );
  // }
  else if (error_code === noPhoneNumber) {
    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: formControlLabels?.phoneNumber,
          name: formControlNames?.phone,
          isRequired: true,
          control,
          rules: {
            ...reactHookFormRegisterOptions?.phone,
            validate: async (value) => {
              // Perform phone number validation
              const validationResult = await validatePhoneNumber(value, true);

              // Return an error message if validation fails
              if (validationResult !== true) {
                return validationResult; // This will set the error in react-hook-form
              }
              return true; // Return true if validation passes
            },
          },
          errors,
          errorCode: noPhoneNumber,
        })}
      </>
    );
  } else if (error_code === noBillingContact) {
    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: formControlLabels?.billingContacts,
          name: formControlNames?.billingContacts,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.billingContacts,
          errors,
          errorCode: noBillingContact,
          options: billingContactsOptions,
        })}
      </>
    );
  } else if ([noLeadAttorney, noLeadAssociate, noLeadParalegal, noLeadCaseManager]?.includes(error_code)) {
    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: formControlLabels?.teamMembers,
          name: formControlNames?.teamMembers,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.teamMembers,
          errors,
          errorCode: noLeadTeamMember,
          options: teamMembersList?.availableTeamMembersWithoutRoles,
        })}
      </>
    );
  } else if ([userArchived, userUnconfirmed, groupMembersArchived, groupMembersUnconfirmed]?.includes(error_code)) {
    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: generateDynamicFormControlsLabel(errorFixDialogData, formControlLabels),
          name: formControlNames?.firmUsers,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.firmUsers,
          errors,
          errorCode: generateDynamicFormControlsErrorCode(errorFixDialogData),
          options: fullAttendeesList,
        })}
      </>
    );
  } else if ([senderArchived, senderUnconfirmed]?.includes(error_code)) {
    conditionBasedUi = (
      <>
        {generateDynamicFormControls({
          label: generateDynamicFormControlsLabel(errorFixDialogData, formControlLabels),
          name: formControlNames?.senderUser,
          isRequired: true,
          control,
          rules: reactHookFormRegisterOptions?.senderUser,
          errors,
          errorCode: generateDynamicFormControlsErrorCode(errorFixDialogData),
          options: messageRecipientsList,
        })}
      </>
    );
  }
  return conditionBasedUi;
};

/**
 * This is Main Dialog Form Component - Here we renders all the dynamically created form according to each error code
 * All logics of this page starts from this ErrorFixDialogFormFacets component
 **/
const ErrorFixDialogFormFacets = (props) => {
  const {
    errorFixDialogData = {},
    onHideDialog = () => {},
    setShowFixErrorConfirmDialog = () => {},
    reactHookForm: { handleSubmit },
  } = props || {};

  const { error_message = 'N/A', is_lead, case_name = 'N/A', action_label = 'N/A' } = errorFixDialogData || {};

  const handleDialogFixErrorButtonClick = (e) => {
    setShowFixErrorConfirmDialog(true);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleDialogFixErrorButtonClick)}>
        <div className="">
          <h1 className="modal-heading F-size16 text-bold pb-3">
            {FIX_ERROR}
            {generateErrorCodeLabel(errorFixDialogData)}
          </h1>
          <div className="mt-4">
            <div className="automation-error-details my-2">
              <i className="icon-Alert me-2"></i>
              <span className="F-size14">{error_message}</span>
            </div>

            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">
                <label htmlFor="">
                  {is_lead ? 'Lead' : 'Case'}
                  {` Name`}
                </label>
              </div>
              <div className="col-lg-8  col-12  d-flex align-items-center">
                <span className="d-tag p-tag p-tag-value">{case_name}</span>
              </div>
            </div>

            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">
                <label htmlFor="">Action Name</label>
              </div>
              <div className="col-lg-8  col-12  d-flex align-items-center">
                <span>{action_label}</span>
              </div>
            </div>
            {displayUiErrorCodeWise({
              ...props,
            })}
          </div>

          <div className="footer-btn d-flex justify-content-end">
            <div className="cancel-container mt-2">
              <PrimeReactButton
                className="btn p-button-secondary outline py-1 px-3"
                type="button"
                label={'Cancel'}
                onButtonClick={onHideDialog}
              />
            </div>
            <div className="fix-button-container mt-2 ms-2">
              <PrimeReactButton className="btn-outline p-button-secondary py-1 px-3" type="submit" label={'Apply Fix'} />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ErrorFixDialogFormFacets;
